import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Outlet, useNavigate, useLocation, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContextNew";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  LayoutDashboard,
  Shield,
  GraduationCap,
  BookOpen,
  Users,
  Heart,
  Calendar,
  FileText,
  BarChart3,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Bell,
  ClipboardList,
  Brain,
  AlertTriangle,
  UserPlus,
  Briefcase,
  Target,
  Menu,
  Smile,
  TrendingUp,
  Home,
  MessageSquare,
  BookOpenCheck,
  HandHelping,
  Check,
  Loader2,
  ScrollText,
  Megaphone,
  CalendarCheck,
  MessageCircle,
  Clock,
  PenLine,
  HelpCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { notificationsAPI } from "@/lib/api";

interface NavItem {
  title: string;
  href: string;
  icon: React.ReactNode;
  badge?: string | number;
  roles: string[]; // Which roles can see this item
}

interface NavSection {
  title: string;
  items: NavItem[];
  roles: string[]; // Which roles can see this section
}

const ALL_ROLES = ["admin", "principal", "teacher", "counsellor", "student", "parent"];
const STAFF_ROLES = ["admin", "principal", "teacher", "counsellor"];
const ADMIN_ROLES = ["admin", "principal"];

const navSections: NavSection[] = [
  {
    title: "MAIN",
    roles: ALL_ROLES,
    items: [
      { title: "Dashboard", href: "/dashboard", icon: <LayoutDashboard className="w-5 h-5" />, roles: ALL_ROLES },
      { title: "Announcements", href: "/announcements", icon: <Megaphone className="w-5 h-5" />, roles: ALL_ROLES },
      { title: "Timetable", href: "/timetable", icon: <Clock className="w-5 h-5" />, roles: ALL_ROLES },
    ],
  },
  {
    title: "COMPLIANCE",
    roles: ADMIN_ROLES,
    items: [
      { title: "Training Tracker", href: "/training", icon: <ClipboardList className="w-5 h-5" />, roles: ADMIN_ROLES },
      { title: "Interventions", href: "/interventions", icon: <Shield className="w-5 h-5" />, roles: ["admin", "principal", "counsellor"] },
      { title: "Annual Reports", href: "/reports", icon: <FileText className="w-5 h-5" />, roles: ADMIN_ROLES },
    ],
  },
  {
    title: "CLASSROOM",
    roles: ["admin", "principal", "teacher", "counsellor"],
    items: [
      { title: "SEL Planner", href: "/sel-planner", icon: <Brain className="w-5 h-5" />, roles: ["admin", "principal", "teacher"] },
      { title: "Holistic Evaluation", href: "/holistic-evaluation", icon: <Target className="w-5 h-5" />, roles: ["admin", "principal", "teacher", "counsellor"] },
      { title: "Referrals", href: "/referrals", icon: <AlertTriangle className="w-5 h-5" />, roles: ["admin", "principal", "teacher", "counsellor"] },
    ],
  },
  {
    title: "PARENTS & COMMUNITY",
    roles: ["admin", "principal", "counsellor", "parent"],
    items: [
      { title: "Sensitization Events", href: "/events", icon: <Calendar className="w-5 h-5" />, roles: ["admin", "principal", "counsellor", "parent"] },
      { title: "Counselling", href: "/counselling", icon: <Heart className="w-5 h-5" />, roles: ["admin", "principal", "counsellor"] },
    ],
  },
  {
    title: "WELLNESS",
    roles: ["student", "counsellor", "admin", "principal", "teacher"],
    items: [
      { title: "Mood Check-in", href: "/mood-check-in", icon: <Smile className="w-5 h-5" />, roles: ["student", "counsellor", "admin", "principal", "teacher"] },
      { title: "Career Explorer", href: "/career-explorer", icon: <Briefcase className="w-5 h-5" />, roles: ["student", "counsellor", "admin", "principal"] },
      { title: "My Counselling", href: "/my-counselling", icon: <MessageSquare className="w-5 h-5" />, roles: ["student"] },
      { title: "Daily Journal", href: "/journal", icon: <PenLine className="w-5 h-5" />, roles: ["student"] },
    ],
  },
  {
    title: "MY CHILD",
    roles: ["parent"],
    items: [
      { title: "Child Progress", href: "/child-progress", icon: <TrendingUp className="w-5 h-5" />, roles: ["parent"] },
      { title: "Counselling", href: "/counselling", icon: <Heart className="w-5 h-5" />, roles: ["parent"] },
      { title: "Attendance", href: "/attendance", icon: <CalendarCheck className="w-5 h-5" />, roles: ["parent"] },
    ],
  },
  {
    title: "MANAGEMENT",
    roles: [...ADMIN_ROLES, "teacher", "counsellor"],
    items: [
      { title: "Users", href: "/users", icon: <Users className="w-5 h-5" />, roles: ADMIN_ROLES },
      { title: "Analytics", href: "/analytics", icon: <BarChart3 className="w-5 h-5" />, roles: ADMIN_ROLES },
      { title: "Attendance", href: "/attendance", icon: <CalendarCheck className="w-5 h-5" />, roles: [...ADMIN_ROLES, "teacher"] },
      { title: "Notifications Center", href: "/notifications-manage", icon: <Bell className="w-5 h-5" />, roles: [...ADMIN_ROLES, "teacher", "counsellor"] },
      { title: "Audit Log", href: "/audit-log", icon: <ScrollText className="w-5 h-5" />, roles: ADMIN_ROLES },
      { title: "Settings", href: "/settings", icon: <Settings className="w-5 h-5" />, roles: ADMIN_ROLES },
    ],
  },
  {
    title: "SUPPORT",
    roles: ALL_ROLES,
    items: [
      { title: "Feedback & Help", href: "/feedback", icon: <HelpCircle className="w-5 h-5" />, roles: ALL_ROLES },
    ],
  },
];

export default function DashboardLayout() {
  const { user, logout, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState<Array<{ _id: string; type: string; title: string; message: string; fromUser?: { name: string; role: string; studentClass?: string; section?: string }; readBy: string[]; createdAt: string }>>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [mentorRequesting, setMentorRequesting] = useState(false);
  const [mentorSent, setMentorSent] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await notificationsAPI.getAll();
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unreadCount || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, fetchNotifications]);

  const handleMentorRequest = async () => {
    setMentorRequesting(true);
    try {
      await notificationsAPI.requestMentor();
      setMentorSent(true);
      setTimeout(() => setMentorSent(false), 5000);
    } catch { /* ignore */ }
    setMentorRequesting(false);
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, readBy: [...n.readBy, user?._id || ""] })));
    } catch { /* ignore */ }
  };

  // Filter nav sections based on user role
  const filteredSections = useMemo(() => {
    const role = user?.role || "";
    return navSections
      .filter((section) => section.roles.includes(role))
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => item.roles.includes(role)),
      }))
      .filter((section) => section.items.length > 0);
  }, [user?.role]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const initials = user?.name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase() || "U";

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b">
        <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <Brain className="w-6 h-6 text-white" />
        </div>
        {!collapsed && (
          <div className="overflow-hidden">
            <h1 className="text-base font-bold text-gray-900 truncate">MVA Burhanpur</h1>
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Wellness Platform</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <ScrollArea className="flex-1 px-3 py-4">
        <div className="space-y-6">
          {filteredSections.map((section) => (
            <div key={section.title}>
              {!collapsed && (
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 mb-2">
                  {section.title}
                </p>
              )}
              <div className="space-y-1">
                {section.items.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <TooltipProvider key={item.href} delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Link
                            to={item.href}
                            onClick={() => setMobileOpen(false)}
                            className={cn(
                              "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                              isActive
                                ? "bg-indigo-50 text-indigo-700 shadow-sm"
                                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                              collapsed && "justify-center px-2"
                            )}
                          >
                            <span className={cn(isActive && "text-indigo-600")}>
                              {item.icon}
                            </span>
                            {!collapsed && (
                              <>
                                <span className="flex-1">{item.title}</span>
                                {item.badge && (
                                  <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                                    {item.badge}
                                  </Badge>
                                )}
                              </>
                            )}
                          </Link>
                        </TooltipTrigger>
                        {collapsed && (
                          <TooltipContent side="right">
                            <p>{item.title}</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* User section */}
      <div className="border-t p-3 space-y-2">
        {/* Access Mentor button for students */}
        {user?.role === "student" && (
          <Button
            onClick={handleMentorRequest}
            disabled={mentorRequesting || mentorSent}
            className={cn(
              "w-full gap-2 font-medium transition-all",
              mentorSent
                ? "bg-emerald-500 hover:bg-emerald-600"
                : "bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700",
              collapsed && "px-2"
            )}
          >
            {mentorRequesting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : mentorSent ? (
              <Check className="w-4 h-4" />
            ) : (
              <HandHelping className="w-4 h-4" />
            )}
            {!collapsed && (
              <span>{mentorSent ? "Request Sent!" : "Access Mentor"}</span>
            )}
          </Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className={cn("flex items-center gap-3 w-full px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors", collapsed && "justify-center px-2")}>
              <Avatar className="w-8 h-8 flex-shrink-0">
                <AvatarFallback className="bg-indigo-100 text-indigo-700 text-xs font-bold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              {!collapsed && (
                <div className="overflow-hidden text-left">
                  <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                  <p className="text-[10px] text-gray-500 capitalize">{user?.role}</p>
                </div>
              )}
              {!collapsed && <LogOut className="w-4 h-4 text-gray-400 ml-auto flex-shrink-0" />}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/dashboard/settings")}>
              <Settings className="w-4 h-4 mr-2" /> Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-red-600">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  return (
    <div className={cn("flex h-screen bg-gray-50")}>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar - Mobile */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 bg-white border-r shadow-xl transform transition-transform lg:hidden",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
          "w-64"
        )}
      >
        <SidebarContent />
      </aside>

      {/* Sidebar - Desktop */}
      <aside
        className={cn(
          "hidden lg:flex flex-col bg-white border-r transition-all duration-300 flex-shrink-0",
          collapsed ? "w-[70px]" : "w-64"
        )}
      >
        <SidebarContent />
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 w-6 h-6 bg-white border rounded-full flex items-center justify-center shadow-md hover:bg-gray-50 z-10"
        >
          {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
        </button>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="h-16 bg-white border-b flex items-center justify-between px-4 lg:px-6 flex-shrink-0">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)}>
              <Menu className="w-5 h-5" />
            </Button>
            <h2 className="text-lg font-semibold text-gray-800 hidden md:block">
              {filteredSections.flatMap(s => s.items).find(i => i.href === location.pathname)?.title || "Dashboard"}
            </h2>
          </div>

          <div className="flex items-center gap-2">
            {/* Notification bell with dropdown */}
            <DropdownMenu onOpenChange={(open) => { if (open) fetchNotifications(); }}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative">
                  <Bell className="w-4 h-4" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 max-h-96">
                <div className="flex items-center justify-between px-3 py-2">
                  <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
                  {unreadCount > 0 && (
                    <button onClick={handleMarkAllRead} className="text-xs text-indigo-600 hover:underline">
                      Mark all read
                    </button>
                  )}
                </div>
                <DropdownMenuSeparator />
                <ScrollArea className="max-h-72">
                  {notifications.length > 0 ? (
                    notifications.slice(0, 10).map((n) => {
                      const isRead = n.readBy.includes(user?._id || "");
                      return (
                        <DropdownMenuItem
                          key={n._id}
                          className={cn("flex flex-col items-start gap-1 px-3 py-2.5 cursor-pointer", !isRead && "bg-indigo-50/50")}
                          onClick={async () => {
                            if (!isRead) {
                              await notificationsAPI.markRead(n._id);
                              fetchNotifications();
                            }
                          }}
                        >
                          <div className="flex items-center gap-2 w-full">
                            <div className={cn("w-2 h-2 rounded-full flex-shrink-0", !isRead ? "bg-indigo-500" : "bg-transparent")} />
                            <span className="text-sm font-medium text-gray-900 flex-1 truncate">{n.title}</span>
                            <Badge variant="outline" className="text-[10px] px-1 capitalize flex-shrink-0">{n.type.replace("-", " ")}</Badge>
                          </div>
                          <p className="text-xs text-gray-500 pl-4 line-clamp-2">{n.message}</p>
                          <div className="flex items-center gap-2 pl-4">
                            {n.fromUser && <span className="text-[10px] text-gray-400">{n.fromUser.name}</span>}
                            <span className="text-[10px] text-gray-400">{new Date(n.createdAt).toLocaleDateString()}</span>
                          </div>
                        </DropdownMenuItem>
                      );
                    })
                  ) : (
                    <div className="text-center py-6 text-sm text-gray-500">No notifications</div>
                  )}
                </ScrollArea>
              </DropdownMenuContent>
            </DropdownMenu>
            <Separator orientation="vertical" className="h-6 mx-1" />
            <div className="flex items-center gap-2">
              <Avatar className="w-8 h-8">
                <AvatarFallback className="bg-indigo-100 text-indigo-700 text-xs font-bold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="hidden md:block">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-[10px] text-gray-500 capitalize">{user?.role}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
